import { useState, useEffect, useRef, useMemo } from 'react'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import type { PostListItem, ViewMode } from '../types'
import {
  doAllChannelsSupportGridView,
  isGridViewSupported,
} from '../utils/gridViewSupport'

export const UseViewMode_Channel = graphql(`
  fragment UseViewMode_Channel on Channel @_unmask {
    service
  }
`)

type UseViewModeParams = {
  posts: PostListItem[]
  channels: FragmentOf<typeof UseViewMode_Channel>[]
}

type UseViewModeResult = {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  isGridViewAvailable: boolean
}

/**
 * Manages view mode state (list/grid) and determines grid view availability.
 *
 * Grid view is available when:
 * - Every channel in the current scope supports grid view (TikTok, YouTube, Instagram)
 * - At least one post supports grid view
 *
 * Automatically switches to grid view when it becomes available (once per session).
 */
export function useViewMode({
  posts,
  channels,
}: UseViewModeParams): UseViewModeResult {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const hasSetDefaultToGrid = useRef(false)

  const isGridViewAvailable = useMemo(() => {
    if (posts.length === 0 || !doAllChannelsSupportGridView(channels)) {
      return false
    }

    return posts.some((post) =>
      isGridViewSupported(post.resolved.channelService),
    )
  }, [posts, channels])

  // Auto-switch to grid view when it becomes available (only once)
  // Reset to list view when grid becomes unavailable
  useEffect(() => {
    if (isGridViewAvailable && !hasSetDefaultToGrid.current) {
      setViewMode('grid')
      hasSetDefaultToGrid.current = true
    } else if (!isGridViewAvailable) {
      setViewMode('list')
      hasSetDefaultToGrid.current = false
    }
  }, [isGridViewAvailable])

  return { viewMode, setViewMode, isGridViewAvailable }
}
